// Helper utilities extracted from WorkflowViews for normalization and data shaping

export function extractSkillFromTargetWorkers(expr) {
  const match = expr?.match(/"([^"]+)"/);
  return match ? match[1] : null;
}

export function normalizeSkill(skill) {
  if (!skill) return '';
  return skill.toLowerCase().replace(/[_\s]+/g, ' ').trim();
}

export function buildWorkflowQueuePriorityData(workflows, taskQueues) {
  const priorityData = [];
  const queueMap = {};
  taskQueues.forEach(q => { queueMap[q.sid] = q; });

  workflows.forEach(wf => {
    try {
      const config = JSON.parse(wf.configuration);
      config.task_routing?.filters?.forEach(filter => {
        filter.targets?.forEach((target, idx) => {
          const queueSid = target.queue;
          const priority = target.priority ?? 0;
          const queueName = queueMap[queueSid]?.friendlyName || 'Unknown';
          priorityData.push({
            workflowName: wf.friendlyName,
            queueSid,
            queueName,
            priority,
            targetIndex: idx,
            serviceGroup: wf.serviceGroup || ''
          });
        });
      });
      const defaultQueue = config.task_routing?.default_filter?.queue;
      const defaultPriority = config.task_routing?.default_filter?.priority ?? 0;
      if (defaultQueue) {
        const queueName = queueMap[defaultQueue]?.friendlyName || 'Unknown';
        priorityData.push({
          workflowName: `${wf.friendlyName} (default)`,
          queueSid: defaultQueue,
          queueName,
          priority: defaultPriority,
          targetIndex: -1,
          serviceGroup: wf.serviceGroup || ''
        });
      }
    } catch (err) {
      // swallow parse errors but keep going
      /* eslint-disable no-console */
      console.error('Failed to parse workflow config:', err.message);
    }
  });
  return priorityData;
}

export function computeQueuesWithoutWorkers(taskQueues, allWorkers) {
  if (!taskQueues.length || !allWorkers.length) return [];
  const allSkills = new Set();
  allWorkers.forEach(worker => {
    try {
      const attrs = JSON.parse(worker.attributes);
      const skills = attrs.routing?.skills || [];
      skills.forEach(skill => allSkills.add(normalizeSkill(skill)));
    } catch { /* ignore */ }
  });
  return taskQueues.filter(q => {
    const skillFromQueue = extractSkillFromTargetWorkers(q.targetWorkers);
    const normalizedSkill = normalizeSkill(skillFromQueue);
    return !allSkills.has(normalizedSkill);
  });
}

export function filterWorkersForQueue(selectedQueueSid, taskQueues, allWorkers) {
  if (!selectedQueueSid) return { workers: [], error: null };
  const selectedQueue = taskQueues.find(q => q.sid === selectedQueueSid);
  if (!selectedQueue) return { workers: [], error: 'Queue not found.' };
  const skillFromQueue = extractSkillFromTargetWorkers(selectedQueue.targetWorkers);
  const normalizedQueueSkill = normalizeSkill(skillFromQueue);
  if (!normalizedQueueSkill) return { workers: [], error: 'Queue skill not found.' };

  const filtered = allWorkers.filter(worker => {
    try {
      const attrs = JSON.parse(worker.attributes);
      return (attrs.routing?.skills || []).some(skill => normalizeSkill(skill) === normalizedQueueSkill);
    } catch { return false; }
  });
  return { workers: filtered, error: null };
}

export function filterWorkflowNames(workflows, teamFilter, searchTerm) {
  return workflows
    .filter(wf => {
      if (teamFilter !== 'all' && !wf.friendlyName.toLowerCase().includes(teamFilter.toLowerCase())) return false;
      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        return wf.friendlyName.toLowerCase().includes(lower) || wf.serviceGroup?.toLowerCase().includes(lower);
      }
      return true;
    })
    .map(wf => wf.friendlyName);
}

export function visibleWorkflowQueuePriorities(workflowQueuePriorities, filteredWorkflowNames) {
  return workflowQueuePriorities
    .filter(entry => !entry.workflowName.endsWith(' (default)'))
    .filter(entry => filteredWorkflowNames.includes(entry.workflowName));
}
